import { Navigate, Route, Routes } from 'react-router-dom'
import Header from './components/Header'
import Menu from './components/Menu'
import Login from './pages/Login'
import './scss/_global-style.scss'
import { useTranslation } from 'react-i18next'
import LiveMap from './pages/LiveMap'
import { ReactNode, useCallback, useContext, useMemo } from 'react'
import UserContext from './contexts/UserContext/context'
import Sidebar from './components/Sidebar'
import SidebarContext from './contexts/SidebarContext/context'
import ProfileSettings from './pages/ProfileSettings'
import Modal from './components/Modal'
import Management from './pages/Management'
import ToastContext from './contexts/ToastContext/context'
import Infobox from './components/Infobox'
import { useToast } from './hooks/useToast'
import History from './pages/History'
import { ICON } from './components/Icon/data'
import Dashboard from './pages/Dashboard'
import Reports from './pages/Reports'
import AreaMonitoring from './pages/AreaMonitoring'
import Maintenance from './pages/Maintenance'
import TooltipContext from './contexts/TooltipContext/context'

type Path = {
  [id: string]: {
    label: string,
    url: string,
    elem: ReactNode,
    icon?: string,
    active_icon?: string,
    permissions: boolean[],
    inMenu?: boolean,
    params?: string,
    preference_key?: string,
  }
}

const App = () => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext)
  const { sidebar } = useContext(SidebarContext)
  const { toast } = useContext(ToastContext)
  const { tooltip } = useContext(TooltipContext)

  const { removeToast } = useToast()
  const { permissions } = user

  const availablePaths: Path = useMemo(() => ({
    DASHBOARD: {
      label: t('Dashboard'),
      url: '/dashboard',
      icon: ICON.METER,
      active_icon: ICON.METER_FILLED,
      elem: <Dashboard />,
      permissions: [!permissions?.trackwell?.cs_dashboard],
      inMenu: true,
      preference_key: 'fleet-dashboard'
    },
    LIVE_MAP: {
      label: t('Live Map'),
      url: '/live-map',
      icon: ICON.GLOBE_NETWORK,
      active_icon: ICON.GLOBE_NETWORK_FILLED,
      elem: <LiveMap />,
      permissions: [!permissions?.trackwell?.cs_livemap],
      inMenu: true,
      preference_key: 'fleet-map'
    },
    REPORTS: {
      label: t('Reports'),
      url: '/reports',
      params: ':type?',
      icon: ICON.METER,
      active_icon: ICON.METER_FILLED,
      elem: <Reports />,
      permissions: [],
      inMenu: true,
    },
    PROFILE_SETTINGS: {
      label: t('Profile settings'),
      url: '/profile-settings',
      elem: <ProfileSettings />,
      permissions: [!permissions?.trackwell?.user_profile]
    },
    MANAGEMENT: {
      label: t('Manage'),
      url: '/manage',
      params: ':view?',
      elem: <Management />,
      permissions: []
    },
    HISTORY: {
      label: t('History'),
      url: '/history',
      icon: ICON.VEHICLE_INSIGHT,
      active_icon: ICON.VEHICLE_INSIGHT_FILLED,
      elem: <History />,
      permissions: [!permissions?.trackwell?.cs_history],
      inMenu: true,
      preference_key: 'fleet-history-map'
    },
    AREA_MONITORING: {
      label: t('Area monitoring'),
      url: '/area-monitoring',
      icon: ICON.ZOOM_AREA,
      active_icon: ICON.ZOOM_AREA_FILLED,
      elem: <AreaMonitoring />,
      permissions: [],
      inMenu: true,
    },
    MAINTENANCE: {
      label: t('Maintenance'),
      url: '/maintenance',
      icon: ICON.TOOL_KIT,
      active_icon: ICON.TOOL_KIT_FILLED,
      elem: <Maintenance />,
      permissions: [!permissions?.trackwell?.cs_maintenance],
      inMenu: true
    }
  }), [t, permissions])

  const authorizedPaths = useMemo(() => {
    return Object.keys(availablePaths).reduce((obj, key) => {
      const path = availablePaths[key]
      if (!path.permissions.length || !path.permissions.includes(false)) obj[key] = path
      return obj
    }, {});
  }, [user.permissions, availablePaths])

  const contentStyles = () => {
    const lookup = {}
    if (sidebar.isOpen) {
      lookup.width = `calc(100% - ${sidebar.width}px)`
    }
    return lookup
  }

  // Cannot be a callback as it will cause hook rerender problem
  const preferencePath = useCallback(() => {
    const prefPath = user?.preferences?.DEFAULT_LANDING_SITE ?? 'fleet-map'
    const foundPath = Object.keys(availablePaths).find((p) => availablePaths[p].preference_key === prefPath)
    return availablePaths[foundPath].url
  }, [availablePaths])

  // If not authenticated throw user to login page
  if (!user.access_token || user?.username?.length === 0) {
    return (
      <>
        <Login />
      </>
    )
  }

  return (
    <div className="App__pages">
      <Menu paths={Object.values(authorizedPaths)} />
      <div className="App__pages-content">
        <Header />
        <div className="App__pages-content__main" style={contentStyles()}>
          <Routes>
            {Object.values(authorizedPaths).map((path) => {
              const { url, elem, params } = path
              const fullPath = `${url}/${params || ''}`
              const isIndex = url === preferencePath()
              if (isIndex) {
                return (
                  <>
                    <Route path="/" element={<Navigate to={url} />} />
                    <Route path={fullPath} element={elem} />
                  </>
                )
              }

              return <Route path={fullPath} element={elem} />
            })}
          </Routes>
          <Sidebar />
          <Sidebar type="infobar" />
          <div className="App__toast">
            {toast?.toasts.map((t, idx) => {
              return <Infobox {...t} type="toast" onClose={() => removeToast(idx)} />
            })}
          </div>
        </div>
      </div>
      <Modal />
      {tooltip.text && tooltip.style && (
        <div className="Tooltip__tooltip" style={tooltip.style} >{tooltip.text}</div>
      )}
    </div>
  )
}

export default App
